import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../app/store";
import { createSession } from "../features/sessions/slice-extensions/sessions-slice-extension";
import { Session } from "../features/sessions/models/Session";
import { PaymentActivityType } from "../features/sessions/models/PaymentActivityType";
import { BookingStatus } from "../features/sessions/models/BookingStatus";
import { mapFrequencyToRecurrenceType } from "../features/sessions/utils/RecurrenceTypeMapper";
import { ROUTES } from "../routes";
import Breadcrumbs from "../components/Breadcrumbs";
import Input from "../components/create-event/Input";
import Textarea from "../components/create-event/Textarea";
import Button from "../components/create-event/Button";
import DatePicker from "../components/create-event/DatePicker";
import TimeSlotSelector from "../components/create-event/TimeSlotSelector";
import LeftMenu from "../components/LeftMenu";

interface FormData {
  eventName: string;
  eventDate: Date;
  startTime: string;
  duration: string;
  frequency: string;
  eventNotes: string;
}

const CreateEvent = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    eventName: "",
    eventDate: new Date(), // Initialize with today's date
    startTime: "",
    duration: "60", // Default to 1 hour
    frequency: "none", // Default to "Does not Repeat"
    eventNotes: "",
  });

  const handleInputChange =
    (field: keyof FormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
    };

  const handleDateSelect = (date: Date) => {
    setFormData((prev) => ({
      ...prev,
      eventDate: date,
    }));
  };

  const handleTimeSelect = (time: string) => {
    setFormData((prev) => ({
      ...prev,
      startTime: time,
    }));
  };

  const handleDurationSelect = (duration: string) => {
    setFormData((prev) => ({
      ...prev,
      duration,
    }));
  };

  const handleFrequencySelect = (frequency: string) => {
    setFormData((prev) => ({
      ...prev,
      frequency,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isLoading) return;

    setIsLoading(true);

    try {
      // Convert form data to Session model
      const sessionData: Session = {
        id: "", // Will be set by the server
        paymentActivityType: PaymentActivityType.Regular,
        title: formData.eventName,
        description: formData.eventNotes || undefined,
        dateStart: formData.eventDate.toISOString(),
        timeLocal: formData.startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        duration: parseInt(formData.duration, 10), // Use selected duration
        isAllDay: false,
        status: BookingStatus.Active,
        recurrenceType: mapFrequencyToRecurrenceType(formData.frequency), // Use selected frequency
        invitees: [],
        dateCreated: new Date().toISOString(),
        dateModified: new Date().toISOString(),
      };

      // Dispatch the create session action
      const result = await dispatch(createSession(sessionData));

      if (createSession.fulfilled.match(result)) {
        console.log("Session created successfully:", result.payload);
        // Redirect to sessions list on success
        navigate(ROUTES.SESSIONS);
      } else {
        console.error("Failed to create session:", result.payload);
      }
    } catch (error) {
      console.error("Error creating session:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(ROUTES.SESSIONS);
  };

  return (
    <section>
      <div className="flex h-full gap-4">
        {/* Left Side */}
        <LeftMenu />
        {/* Right Side */}
        <div className="w-full p-4 pb-10 lg:px-10">
          <div className="lg:max-w-[694px]">
            {/* Breadcrumbs */}
            <Breadcrumbs />

            {/* Content */}
            <div className="space-y-4 py-6 lg:space-y-6">
              <h1>Create Session</h1>

              <form onSubmit={handleSubmit} className="space-y-6 lg:space-y-4">
                {/* Schedule Details */}
                <div className="space-y-6 lg:space-y-5">
                  <h3>Schedule Details</h3>

                  <Input
                    label="Title"
                    type="text"
                    name="event_name"
                    id="event_name"
                    placeholder="What's your session called?"
                    value={formData.eventName}
                    onChange={handleInputChange("eventName")}
                  />
                </div>

                {/* Session Date */}
                <DatePicker
                  selectedDate={formData.eventDate}
                  onDateSelect={handleDateSelect}
                />

                {/* Start Time */}
                <TimeSlotSelector
                  selectedTime={formData.startTime}
                  onTimeSelect={handleTimeSelect}
                  selectedDuration={formData.duration}
                  onDurationSelect={handleDurationSelect}
                  selectedFrequency={formData.frequency}
                  onFrequencySelect={handleFrequencySelect}
                />

                {/* Divider */}
                <hr className="border-grey-70" />

                <div className="space-y-6 lg:space-y-5">
                  <h3>Session Notes</h3>

                  <Textarea
                    label="Notes"
                    name="session_notes"
                    id="session_notes"
                    placeholder="Add some session details here..."
                    value={formData.eventNotes}
                    onChange={handleInputChange("eventNotes")}
                  />
                </div>

                <div className="flex items-start gap-3">
                  <Button type="submit" variant="primary" disabled={isLoading}>
                    {isLoading ? "Creating..." : "Create Session"}
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreateEvent;
